import { Component, inject, signal } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';

import { SessionsApi, UserIdentity, UserIdentityApi } from '@venecia/apis/auth';
import { GlobalConstants } from 'app/shared/constants';
import { CurrentUser, MenuItem, ThemeName } from 'app/shared/models';
import {
  ConfigService,
  ConfirmDialogService,
  ErrorMessageService,
  I18nService,
  MessageService,
  ThemeService,
  TraceService,
  StorageService,
  Language,
  LanguageService,
} from 'app/shared/services';
import { ConfigPipe } from 'app/shared/pipes';
import { UserProfileModal } from '../user-profile-modal/user-profile-modal';

@Component({
  selector: 'app-topbar-dropdown',
  imports: [TranslateModule, NgbDropdownModule, ConfigPipe, UserProfileModal],
  templateUrl: './topbar-dropdown.html',
  styleUrl: './topbar-dropdown.scss',
})
export class TopbarDropdown {
  readonly themeService = inject(ThemeService);

  private readonly i18nService = inject(I18nService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly languageService = inject(LanguageService);
  private readonly storageService = inject(StorageService);
  private readonly configService = inject(ConfigService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly messageService = inject(MessageService);
  private readonly sessionsApi = inject(SessionsApi);
  private readonly traceService = inject(TraceService);
  private readonly userIdentityApi = inject(UserIdentityApi);

  protected readonly theme = signal<ThemeName>('light');

  protected readonly currentLang = signal<string | undefined>(undefined);
  protected readonly supportedLanguages = signal<Language[]>([]);
  protected readonly profileData = signal<UserIdentity | null>(null);
  protected readonly profileLoading = signal(false);
  protected readonly profileOpen = signal(false);
  protected readonly loggingOut = signal(false);
  protected readonly languageMenuOpen = signal(false);

  protected readonly menu: MenuItem[];

  currentUser: CurrentUser | null = null;

  constructor() {
    this.theme.set(this.themeService.get());
    this.languageService.currentLanguage$
      .subscribe(langCode => {
        this.currentLang.set(langCode);
      });
    this.supportedLanguages.set(this.languageService.getSupportedLanguages());

    this.currentUser = this.storageService.get<CurrentUser>(GlobalConstants.CURRENT_USER_KEY);

    this.menu = this.configService.get<MenuItem[]>('menu')
  }

  toggleTheme(): void {
    this.themeService.toggle();
    this.theme.set(this.themeService.get());
  }

  toggleLanguageMenu(): void {
    this.languageMenuOpen.update((value) => !value);
  }

  changeLang(langCode: string): void {
    if (langCode === this.currentLang()) {
      return;
    }

    this.i18nService.changeLanguage(langCode);
    this.currentLang.set(langCode);
    this.languageMenuOpen.set(false);
  }

  isCurrentLang(langCode: string): boolean {
    return this.currentLang() === langCode;
  }

  protected getMenuItem(key: string): MenuItem | undefined {
    return this.menu.filter(menuItem => menuItem.item === key)[0] ?? undefined;
  }

  async openProfileModal(): Promise<void> {
    this.profileOpen.set(true);
    this.profileLoading.set(true);

    try {
      const response = await firstValueFrom(this.userIdentityApi.me());
      this.profileData.set(response.body);
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error));
      this.profileOpen.set(false);
    } finally {
      this.profileLoading.set(false);
    }
  }

  closeProfileModal(): void {
    this.profileOpen.set(false);
  }

  async logout(): Promise<void> {
    if (this.loggingOut()) {
      return;
    }

    this.loggingOut.set(true);

    try {
      const i18n = await this.translateMap([
        'common.confirm-save.cancel',
        'components.topbar.logout.button',
        'components.topbar.logout.confirm',
        'components.topbar.logout.title',
      ] as const);

      const confirmed = await this.confirmDialogService.open({
        cancelText: i18n['common.confirm-save.cancel'],
        confirmText: i18n['components.topbar.logout.button'],
        message: i18n['components.topbar.logout.confirm'],
        title: i18n['components.topbar.logout.title'],
      });

      if (confirmed) {
        await this.executeLogout();
      }
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'components.topbar.logout.error'));
    } finally {
      this.loggingOut.set(false);
    }
  }

  private async executeLogout(): Promise<void> {
    try {
      await firstValueFrom(this.sessionsApi.logout());
    } catch (error) {
      this.traceService.internal(error, true);
    } finally {
      this.storageService.clear();
      window.location.assign('/');
    }
  }

  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translateService.get(key));
  }

  private async translateMap(keys: readonly string[]): Promise<Record<string, string>> {
    const values = await Promise.all(keys.map((key) => this.translate(key)));

    return keys.reduce<Record<string, string>>((acc, key, index) => {
      acc[key] = values[index];
      return acc;
    }, {});
  }
}
